import * as XLSX from 'xlsx'

export interface ExportBomWoodLine {
  part_name: string
  wood_type_raw?: string | null
  material_name?: string | null
  dims_finished_t?: number | null
  dims_finished_w?: number | null
  dims_finished_l?: number | null
  tenon?: string | null
  qty_per: number
  veneer_spec?: string | null
  notes?: string | null
}

export interface ExportBomHardwareLine {
  name_raw: string
  spec_raw?: string | null
  material_name?: string | null
  qty_per: number
  uom_hint?: string | null
  color_hint?: string | null
  notes?: string | null
}

export interface ExportBomExcelParams {
  itemCode: string
  productName: string
  productDimensions?: string
  variantColor?: string
  assemblyType: 'assembled' | 'kd'
  mainWoodType?: string
  veneerSpec?: string
  bomCode?: string
  versionSeq?: number
  editCount?: number
  status?: string
  preparedBy?: string | null
  checkedBy?: string | null
  checkedBy2?: string | null
  approvedBy?: string | null
  signedOn?: string | null
  notes?: string | null
  woodLines: ExportBomWoodLine[]
  hardwareLines: ExportBomHardwareLine[]
}

export function generateBomExcelWorkbook(params: ExportBomExcelParams): XLSX.WorkBook {
  const wb = XLSX.utils.book_new()
  const rows: (string | number)[][] = []

  // 1. Header Information Block
  rows.push(['PHIẾU ĐỊNH MỨC KỸ THUẬT (BOM SHEET) — CÔNG TY MỘC PHÁT'])
  rows.push([`MÃ HÀNG: ${params.itemCode}`])
  rows.push([
    'Tên sản phẩm:',
    params.productName,
    '',
    'Kích thước (D×R×C):',
    params.productDimensions || '—',
    '',
    'Màu sắc (Variant):',
    params.variantColor || 'Mặc định',
  ])
  rows.push([
    'Chế độ lắp ráp:',
    params.assemblyType === 'assembled' ? 'RÁP CHẾT (FA)' : 'Tháo rời (KD)',
    '',
    'Loại gỗ chính:',
    params.mainWoodType || 'Theo chi tiết',
    '',
    'Quy cách Veneer:',
    params.veneerSpec || '—',
  ])
  rows.push([
    'Mã định mức (BOM):',
    params.bomCode || params.itemCode,
    '',
    'Phiên bản:',
    `v${params.versionSeq ?? 1}${
      params.editCount && params.editCount > 0 ? ` · Đã sửa lần ${params.editCount}` : ''
    }`,
    '',
    'Trạng thái:',
    params.status === 'active'
      ? 'Đang áp dụng (ACTIVE)'
      : params.status === 'archived'
        ? 'Lưu trữ (ARCHIVED)'
        : 'Bản nháp (INACTIVE)',
  ])
  rows.push([
    'Người lập:',
    params.preparedBy || '—',
    'Kiểm tra 1:',
    params.checkedBy || '—',
    'Kiểm tra 2:',
    params.checkedBy2 || '—',
    'Duyệt:',
    params.approvedBy || '—',
    'Ngày ký:',
    params.signedOn || '—',
  ])
  if (params.notes) {
    rows.push(['Ghi chú BOM:', params.notes])
  }
  rows.push([]) // Blank separator

  // 2. Table 1: WOOD LINES
  rows.push(['BẢNG 1 — ĐỊNH MỨC NGUYÊN VẬT LIỆU GỖ (Đơn vị: 01 sản phẩm)'])
  rows.push([
    'STT',
    'Tên chi tiết',
    'Loại gỗ / phôi',
    'Dày (mm)',
    'Rộng (mm)',
    'Dài (mm)',
    'Mộng',
    'SL/SP',
    'M³ tinh',
    'Quy cách veneer',
    'Ghi chú',
  ])

  // Grouping map for wood summary footer
  const woodGroups = new Map<
    string,
    { totalM3: number; partCount: number; qtyTotal: number }
  >()
  let grandTotalM3 = 0
  let totalWoodParts = 0
  let totalWoodQty = 0
  let missingDimsCount = 0

  params.woodLines.forEach((w, idx) => {
    const t = w.dims_finished_t != null ? Number(w.dims_finished_t) : null
    const width = w.dims_finished_w != null ? Number(w.dims_finished_w) : null
    const len = w.dims_finished_l != null ? Number(w.dims_finished_l) : null
    const qty = Number(w.qty_per) || 1

    let lineM3: number | null = null
    if (t != null && width != null && len != null) {
      lineM3 = (t * width * len * qty) / 1e9
      grandTotalM3 += lineM3
    } else {
      missingDimsCount++
    }

    const groupKey = w.wood_type_raw || w.material_name || 'Khác'
    const existing = woodGroups.get(groupKey) ?? { totalM3: 0, partCount: 0, qtyTotal: 0 }
    woodGroups.set(groupKey, {
      totalM3: existing.totalM3 + (lineM3 ?? 0),
      partCount: existing.partCount + 1,
      qtyTotal: existing.qtyTotal + qty,
    })

    totalWoodParts++
    totalWoodQty += qty

    rows.push([
      idx + 1,
      w.part_name,
      w.wood_type_raw || w.material_name || '—',
      t != null ? t : '',
      width != null ? width : '',
      len != null ? len : '',
      w.tenon || '',
      qty,
      lineM3 != null ? Number(lineM3.toFixed(4)) : '—',
      w.veneer_spec || '',
      w.notes || '',
    ])
  })

  // Footers of Table 1: Grouped Net M3 by wood type
  woodGroups.forEach((val, type) => {
    rows.push([
      '',
      `› ${type}`,
      '',
      '',
      '',
      '',
      '',
      `${val.partCount} chi tiết (${val.qtyTotal} sp)`,
      Number(val.totalM3.toFixed(4)),
      'M³',
      '',
    ])
  })

  // Grand Total M3 row
  rows.push([
    '',
    'TỔNG M³ (CBM)',
    '',
    '',
    '',
    '',
    '',
    `${totalWoodParts} chi tiết (${totalWoodQty} sp)`,
    Number(grandTotalM3.toFixed(4)),
    'M³ (Tổng)',
    '',
  ])

  if (missingDimsCount > 0) {
    rows.push([
      `* Lưu ý: Có ${missingDimsCount} chi tiết thiếu kích thước nên chưa tính đủ M³ tinh.`,
    ])
  }

  rows.push([]) // Blank separator

  // 3. Table 2: HARDWARE LINES
  rows.push(['BẢNG 2 — ĐỊNH MỨC NGŨ KIM (Đơn vị: 01 sản phẩm)'])
  rows.push([
    'STT',
    'Tên vật tư / quy cách',
    'Quy cách kỹ thuật',
    'Số lượng (SL/SP)',
    'Đơn vị',
    'Màu sắc',
    'Phân loại (SX/KD)',
    'Vị trí lắp đặt / Ghi chú',
  ])

  let totalHwQty = 0
  params.hardwareLines.forEach((h, idx) => {
    const qty = Number(h.qty_per) || 1
    totalHwQty += qty
    rows.push([
      idx + 1,
      h.name_raw,
      h.spec_raw || '—',
      qty,
      h.uom_hint || 'Cái',
      h.color_hint || '—',
      params.assemblyType === 'assembled' ? 'FA (RÁP CHẾT)' : 'KD (Tháo rời)',
      h.notes || '',
    ])
  })

  rows.push([
    '',
    'TỔNG CỘNG NGŨ KIM',
    '',
    totalHwQty,
    'chi tiết / cái',
    '',
    '',
    '',
  ])

  // Sheet creation
  const safeSheetName = `BOM-${params.itemCode}`.slice(0, 31).replace(/[\\/?*[\]]/g, '-')
  const ws = XLSX.utils.aoa_to_sheet(rows)

  // Formatting column widths
  ws['!cols'] = [
    { wch: 6 }, // STT
    { wch: 28 }, // Tên chi tiết / Tên vật tư
    { wch: 18 }, // Loại gỗ / Quy cách kỹ thuật
    { wch: 12 }, // Dày / SL
    { wch: 12 }, // Rộng / ĐVT
    { wch: 12 }, // Dài / Màu sắc
    { wch: 14 }, // Mộng / Phân loại
    { wch: 16 }, // SL/SP / Ghi chú
    { wch: 14 }, // M3 tinh
    { wch: 18 }, // Veneer
    { wch: 22 }, // Ghi chú
  ]

  XLSX.utils.book_append_sheet(wb, ws, safeSheetName)
  return wb
}

export function exportBomToExcel(params: ExportBomExcelParams, customFilename?: string): void {
  const wb = generateBomExcelWorkbook(params)
  const defaultName = `${params.itemCode}_BOM_${params.assemblyType}_v${params.versionSeq ?? 1}.xlsx`
  XLSX.writeFile(wb, customFilename || defaultName)
}
